import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'

interface ThemeContextType {
  theme: 'light' | 'dark'
  toggleTheme: () => void
  isDesktop: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [isDesktop, setIsDesktop] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // Check if device is desktop (> 768px)
  const checkIsDesktop = useCallback(() => {
    return window.innerWidth > 768
  }, [])

  // Apply theme class to document root
  const applyTheme = useCallback((newTheme: 'light' | 'dark') => {
    console.log('Applying theme:', newTheme) // Debug log
    const htmlElement = document.documentElement
    
    // Remove both classes first to ensure clean state
    htmlElement.classList.remove('dark', 'light')
    
    // Add the appropriate class
    if (newTheme === 'dark') {
      htmlElement.classList.add('dark')
    } else {
      htmlElement.classList.add('light')
    }
    
    console.log('Dark class applied:', htmlElement.classList.contains('dark')) // Debug log
    console.log('Light class applied:', htmlElement.classList.contains('light')) // Debug log
    console.log('All classes on html:', Array.from(htmlElement.classList)) // Debug log
  }, [])

  // Initialize theme based on device type
  const initializeTheme = useCallback(() => {
    const desktop = checkIsDesktop()
    setIsDesktop(desktop)

    if (desktop) {
      // Desktop: Use saved preference or default to light
      const savedTheme = localStorage.getItem('theme-preference') as 'light' | 'dark' | null
      const newTheme = savedTheme || 'light'
      console.log('Desktop theme initialized:', newTheme) // Debug log
      setTheme(newTheme)
      applyTheme(newTheme)
    } else {
      // Mobile: Follow system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      const newTheme = prefersDark ? 'dark' : 'light'
      console.log('Mobile theme initialized (system):', newTheme) // Debug log
      setTheme(newTheme)
      applyTheme(newTheme)
    }
    setIsInitialized(true)
  }, [checkIsDesktop, applyTheme])

  // Toggle theme (only works on desktop)
  const toggleTheme = useCallback(() => {
    if (!isDesktop) {
      console.log('Theme toggle blocked: mobile device')
      return // Don't allow toggle on mobile
    }

    const newTheme = theme === 'light' ? 'dark' : 'light'
    console.log('Toggling theme from', theme, 'to', newTheme) // Debug log
    setTheme(newTheme)
    applyTheme(newTheme)
    localStorage.setItem('theme-preference', newTheme)
  }, [isDesktop, theme, applyTheme])

  // Initialize theme on component mount
  useEffect(() => {
    initializeTheme()
  }, [initializeTheme])

  // Listen for window resize to detect desktop/mobile switch
  useEffect(() => {
    const handleResize = () => {
      const wasDesktop = isDesktop
      const nowDesktop = checkIsDesktop()
      
      if (wasDesktop !== nowDesktop) {
        setIsDesktop(nowDesktop)
        initializeTheme() // Re-initialize theme when switching between desktop/mobile
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isDesktop, checkIsDesktop, initializeTheme])

  // Listen for system theme changes (mobile only)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (!checkIsDesktop()) {
        const newTheme = e.matches ? 'dark' : 'light'
        console.log('System theme changed (mobile):', newTheme) // Debug log
        setTheme(newTheme)
        applyTheme(newTheme)
      }
    }

    mediaQuery.addEventListener('change', handleSystemThemeChange)

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange)
    }
  }, [checkIsDesktop, applyTheme])

  // Apply theme whenever theme state changes
  useEffect(() => {
    if (isInitialized) {
      applyTheme(theme)
    }
  }, [theme, isInitialized, applyTheme])

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDesktop }}>
      {children}
    </ThemeContext.Provider>
  )
}